import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ThresholdActions } from './threshold.actions';
import { ThresholdService } from '../../service/threshold.service';
import { mergeMap, map, catchError, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { Threshold, RiskThreshold, IcsEvaluationWeights } from '../../../../graphql/generated';

@Injectable()
export class ThresholdEffects {


  loadTreshold$ = createEffect(
    (rs: ThresholdService = inject(ThresholdService)) => {
      return this.actions$.pipe(

        ofType(ThresholdActions.loadThreshold),
        mergeMap((parameters) =>
          rs.getTreshold().pipe(
            map((Threshold) => {
              return ThresholdActions.loadThresholdSuccess({
                ics: Threshold.ics as Threshold[],
                likelihoods: Threshold.likelihoods as Threshold[],
                impacts: Threshold.impacts as Threshold[],
                inerentRisk: Threshold.inherentRisks as RiskThreshold[],
                residualRisk: Threshold.residualRisks as RiskThreshold[],
                gias: (Threshold.giasRatingscale || []) as any[],
                kirsRatingscale: (Threshold.kirsRatingScale || []) as any[]
              });
            }),
            catchError((error) => of(ThresholdActions.loadThresholdFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadIcsWeight$ = createEffect(
    (rs: ThresholdService = inject(ThresholdService)) => {
      return this.actions$.pipe(

        ofType(ThresholdActions.loadIcsWeight),
        mergeMap((parameters) =>
          rs.getIcsWeights(parameters.icsCampaignId).pipe(
            map((icsEvaluation) => {
              return ThresholdActions.loadIcsWeightSuccess(

                {
                  icsEvaluation: icsEvaluation as any
                }
              );
            }),
            catchError((error) => of(ThresholdActions.loadIcsWeightFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );




  // Pesi e scale dei KPI vanno nella stessa mutation: la risposta contiene entrambi, quindi basta un solo success.
  updateIcsWeight$ = createEffect(
    (rs: ThresholdService = inject(ThresholdService)) => {
      return this.actions$.pipe(

        ofType(ThresholdActions.updateIcsWeight),
        mergeMap(({ icsCampaignId, weights, thresholds }) =>
          rs.updateIcsWeights(icsCampaignId, weights, thresholds).pipe(
            map((res) => {
              // 400 = validazione rifiutata (bande incoerenti o pesi != 100): niente e' stato scritto e `data` e' null.
              if (res?.statusCode === 400) {
                return ThresholdActions.updateIcsWeightFailure({
                  error: res?.message ?? 'Update rejected: no changes were saved'
                });
              }
              return ThresholdActions.updateIcsWeightSuccess({
                icsEvaluation: res?.data?.[0] as IcsEvaluationWeights
              });
            }),
            catchError((error) => of(ThresholdActions.updateIcsWeightFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateIcsWeightFailureMessage$ = createEffect(
    (messageService: MessageService = inject(MessageService)) => {
      return this.actions$.pipe(
        ofType(ThresholdActions.updateIcsWeightFailure),
        map(({ error }) => {
          messageService.add({
            severity: 'error',
            summary: 'Update rejected',
            detail: typeof error === 'string' ? error : 'No changes were saved',
            life: 5000
          });
        })
      );
    },
    { functional: true, dispatch: false }
  );

  updateThresholdICS$ = createEffect(
    (rs: ThresholdService = inject(ThresholdService)) => {
      return this.actions$.pipe(

        ofType(ThresholdActions.updateThresholdIcs),
        mergeMap((parameters) =>
          rs.updateThresholdIcs(parameters.id, parameters.min, parameters.max, parameters.label, parameters.extendedLabel).pipe(
            map((data) => {
              return ThresholdActions.loadThreshold(
              );
            }),
            catchError((error) => of(ThresholdActions.updateThresholdIcsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateThresholdLike$ = createEffect(
    (rs: ThresholdService = inject(ThresholdService)) => {
      return this.actions$.pipe(

        ofType(ThresholdActions.updateThresholdLike),
        mergeMap((parameters) =>
          rs.updateThresholdLike(parameters.id, parameters.min, parameters.max, parameters.label, parameters.extendedLabel).pipe(
            map((data) => {
              return ThresholdActions.loadThreshold(
              );
            }),
            catchError((error) => of(ThresholdActions.updateThresholdLikeFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateThresholdImpact$ = createEffect(
    (rs: ThresholdService = inject(ThresholdService)) => {
      return this.actions$.pipe(

        ofType(ThresholdActions.updateThresholdImpact),
        mergeMap((parameters) =>
          rs.updateThresholdImpact(parameters.id, parameters.min, parameters.max, parameters.label, parameters.extendedLabel).pipe(
            map((data) => {
              return ThresholdActions.loadThreshold(
              );
            }),
            catchError((error) => of(ThresholdActions.updateThresholdImpactFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateInherentRiskThreshold$ = createEffect(
    (rs: ThresholdService = inject(ThresholdService)) => {
      return this.actions$.pipe(

        ofType(ThresholdActions.updateThresholdIR),
        mergeMap((parameters) =>
          rs.updateThresholdIR(parameters.id, parameters.min, parameters.max, parameters.label, parameters.extendedLabel).pipe(
            map((data) => {
              return ThresholdActions.loadThreshold(
              );
            }),
            catchError((error) => of(ThresholdActions.updateThresholdIRFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateResidualRiskThreshold$ = createEffect(
    (rs: ThresholdService = inject(ThresholdService)) => {
      return this.actions$.pipe(

        ofType(ThresholdActions.updateThresholdRR),
        mergeMap((parameters) =>
          rs.updateThresholdRR(parameters.id, parameters.min, parameters.max, parameters.label, parameters.extendedLabel).pipe(
            map((data) => {
              return ThresholdActions.loadThreshold(
              );
            }),
            catchError((error) => of(ThresholdActions.updateThresholdRRFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateThresholdGias$ = createEffect(
    (rs: ThresholdService = inject(ThresholdService)) => {
      return this.actions$.pipe(
        ofType(ThresholdActions.updateThresholdGias),
        mergeMap((parameters) =>
          rs.updateThresholdGias(parameters.thresholds).pipe(
            map((data) => {
              return ThresholdActions.loadThreshold();
            }),
            catchError((error) => of(ThresholdActions.updateThresholdGiasFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateThresholdKirs$ = createEffect(
    (rs: ThresholdService = inject(ThresholdService)) => {
      return this.actions$.pipe(
        ofType(ThresholdActions.updateThresholdKirs),
        mergeMap((parameters) =>
          rs.updateThresholdKirs(parameters.thresholds).pipe(
            map((data) => {
              return ThresholdActions.loadThreshold();
            }),
            catchError((error) => of(ThresholdActions.updateThresholdKirsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  constructor(private actions$: Actions) { }
}
